/**
 * HTTP请求工具（重构版 - 极简配置）
 * 统一使用相对路径，由Nginx代理转发
 * 创建时间：2025-12-25
 */

import axios from 'axios'
import { ElMessage } from 'element-plus'
import {
  ensureFreshAccessToken,
  handleSessionExpired,
  isSessionExpiryResponse,
  refreshAccessToken
} from '@/utils/sessionExpiryHandler'

// 创建axios实例 - 统一使用相对路径
const service = axios.create({
  baseURL: process.env.VUE_APP_API_BASE_URL || '/api', // 默认使用相对路径
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json;charset=UTF-8'
  }
})

function isPublicPath(url = '') {
  const path = String(url || '').toLowerCase()
  return path.includes('/login') || path.includes('/auth/') || path.includes('/health')
}

function isAuthRetrySkipped(url = '') {
  const path = String(url || '').toLowerCase()
  return path.includes('/auth/login') || path.includes('/auth/refresh')
}

function createSilentAuthError() {
  const silentError = new Error('会话已过期')
  silentError._isAuthError = true
  silentError._silent = true
  return silentError
}

function shouldSuppressErrorToast(error) {
  const statuses = error?.config?.silentStatuses
  return Array.isArray(statuses) && statuses.includes(error?.response?.status)
}

// 请求拦截器
service.interceptors.request.use(
  async config => {
    config.headers = config.headers || {}
    // 添加时间戳避免缓存
    config.headers['X-Request-Time'] = Date.now()

    // 添加token（如果存在）
    const token = isPublicPath(config.url)
      ? localStorage.getItem('token')
      : await ensureFreshAccessToken()
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`
    } else {
      // 无 token 时直接拦截请求，不发到后端，避免 401 报错暴露端点
      // 登录接口等公开路径不受此限制
      if (!isPublicPath(config.url)) {
        const refreshed = await refreshAccessToken()
        if (refreshed?.token) {
          config.headers['Authorization'] = `Bearer ${refreshed.token}`
        } else {
          return Promise.reject(createSilentAuthError())
        }
      }
    }

    return config
  },
  error => {
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    return response.data
  },
  async error => {
    if (error._isAuthError || error._silent) {
      return Promise.reject(error)
    }

    // 401 会话过期：静默处理，不暴露 API 端点
    if (error.response && error.response.status === 401) {
      const originalConfig = error.config || {}
      if (!originalConfig._authRetry && !isAuthRetrySkipped(originalConfig.url)) {
        originalConfig._authRetry = true
        const refreshed = await refreshAccessToken()
        if (refreshed?.token) {
          originalConfig.headers = originalConfig.headers || {}
          originalConfig.headers.Authorization = `Bearer ${refreshed.token}`
          return service(originalConfig)
        }
      }
      if (isSessionExpiryResponse(error) && !originalConfig._skipSessionExpiryPrompt) {
        await handleSessionExpired()
      }
      // 返回一个被标记为静默的拒绝，避免上层继续输出错误
      return Promise.reject(createSilentAuthError())
    }

    // 某些查询用 404 表示“尚无结果/内存任务已过期”，由调用方按业务语义处理，
    // 不应先被全局拦截器翻译为面向用户的错误弹窗。
    if (shouldSuppressErrorToast(error)) {
      return Promise.reject(error)
    }

    // 非认证错误正常处理
    let message = '请求失败'

    if (error.response) {
      const { status, data } = error.response

      switch (status) {
        case 400:
          message = data.message || '请求参数错误'
          break
        case 403:
          message = '禁止访问'
          break
        case 404:
          message = '请求的资源不存在'
          break
        case 500:
          message = '服务器内部错误'
          break
        case 502:
          message = '网关错误'
          break
        case 503:
          message = '服务不可用'
          break
        default:
          message = data.message || `请求失败 (${status})`
      }
    } else if (error.request) {
      message = '网络连接失败，请检查网络设置'
    } else {
      message = error.message || '未知错误'
    }

    ElMessage.error(message)
    return Promise.reject(error)
  }
)

export default service

/**
 * 创建可取消的请求控制器
 * 组件卸载时调用 cancel() 防止内存泄漏和状态更新
 * @example
 *   const controller = createRequestController()
 *   const data = await service.get('/api/data', { signal: controller.signal })
 *   // 组件卸载时：controller.cancel()
 */
export function createRequestController() {
  const controller = new AbortController()
  return {
    signal: controller.signal,
    cancel: () => controller.abort()
  }
}

// 导出便捷请求方法
export const request = {
  get(url, params, options = {}) {
    return service.get(url, { ...options, params })
  },

  post(url, data) {
    return service.post(url, data)
  },

  put(url, data) {
    return service.put(url, data)
  },

  delete(url, params) {
    return service.delete(url, { params })
  },

  upload(url, formData) {
    return service.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    })
  },

  download(url, params) {
    return service.get(url, {
      params,
      responseType: 'blob'
    })
  }
}
